// src/hooks/usePortalViewReport.js
// SEAM 10 VERIFIER: qview-6b2d4e91-report
//
// The customer portal's side of view tracking. Calls recordQuoteView AT MOST
// ONCE per page load, and only when all of these hold:
//   1. the estimate has rendered (`ready` — the page passes quote-loaded and
//      no load error);
//   2. the tab has been VISIBLE for VIEW_VISIBLE_MS in total;
//   3. a real person has interacted — pointer movement or press, touch,
//      scroll, wheel or a key.
// (2) and (3) are the link-scanner guard (founder decision, 2026-10-01): a
// security scanner that renders the page rarely stays visible and never moves
// a pointer. A customer who opens the page and reads without touching anything
// does not count — accepted.
//
// Never on a staff-preview browser (utils/portalPreview.js). Best-effort and
// silent: a failure is swallowed, and the customer sees nothing either way.
// The server dedupes (30 min) and decides everything else — this hook only
// decides "a person is looking".
import { useEffect, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/config';
import { isStaffPreviewBrowser } from '../utils/portalPreview';

const VIEW_VISIBLE_MS = 5000;
const TICK_MS = 500;
const INTERACTION_EVENTS = ['pointermove', 'pointerdown', 'touchstart', 'scroll', 'wheel', 'keydown'];

export default function usePortalViewReport(quoteId, ready) {
    const reportedRef = useRef(null); // the quoteId already reported this page load

    useEffect(() => {
        if (!ready || !quoteId) return undefined;
        if (reportedRef.current === quoteId) return undefined;
        if (isStaffPreviewBrowser()) return undefined;

        let visibleMs = 0;
        let interacted = false;
        let done = false;

        const finish = () => {
            done = true;
            clearInterval(timer);
            INTERACTION_EVENTS.forEach((e) => window.removeEventListener(e, onInteract, true));
        };
        const maybeReport = () => {
            if (done || !interacted || visibleMs < VIEW_VISIBLE_MS) return;
            reportedRef.current = quoteId;
            finish();
            httpsCallable(functions, 'recordQuoteView')({ quoteId }).catch(() => { /* silent by design */ });
        };
        const onInteract = () => { interacted = true; maybeReport(); };
        const timer = setInterval(() => {
            if (typeof document === 'undefined' || document.visibilityState === 'visible') visibleMs += TICK_MS;
            maybeReport();
        }, TICK_MS);
        // Capture phase so a scroll inside an inner panel counts too.
        INTERACTION_EVENTS.forEach((e) => window.addEventListener(e, onInteract, { capture: true, passive: true }));

        return () => { if (!done) finish(); };
    }, [quoteId, ready]);
}
